import { z } from "zod";
import { im30SaleResponseSchema } from "./im30";

export const KIOSK_TOKEN_HEADER = "X-Kiosk-Token";

const isoDateString = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const handshakeRequestSchema = z.object({
  bridgeVersion: z.string(),
  lastSaleNumberKnown: z.string().optional(),
});
export type HandshakeRequest = z.infer<typeof handshakeRequestSchema>;

export const handshakeResponseSchema = z.object({
  terminalId: z.number().int().positive(),
  museumId: z.number().int().positive(),
  museumName: z.string(),
  museumTimezone: z.string(),
  im30: z.object({
    commerceCode: z.string(),
    terminalIdConfigured: z.string().nullable(),
  }),
  kioskConfig: z.object({
    idleTimeoutSec: z.number().int().positive(),
    attractMediaUrl: z.string().nullable(),
    printerWidthChars: z.number().int().positive(),
    dailyCloseLocalTime: z
      .string()
      .regex(/^\d{2}:\d{2}$/)
      .nullable(),
  }),
});
export type HandshakeResponse = z.infer<typeof handshakeResponseSchema>;

export const museumConfigResponseSchema = z.object({
  museumId: z.number().int().positive(),
  name: z.string(),
  logoUrl: z.string().nullable(),
  themeColor: z.string().nullable(),
  currency: z.literal("CLP"),
  timezone: z.string(),
  termsAndConditions: z.string().nullable(),
});
export type MuseumConfigResponse = z.infer<typeof museumConfigResponseSchema>;

export const ticketTypeForKioskSchema = z.object({
  id: z.number().int().positive(),
  name: z.string(),
  description: z.string().nullable(),
  priceCLP: z.number().int().nonnegative(),
  maxPerOrder: z.number().int().positive().nullable(),
  available: z.boolean(),
  unavailableReason: z.string().nullable(),
});
export type TicketTypeForKiosk = z.infer<typeof ticketTypeForKioskSchema>;

export const ticketTypesQuerySchema = z.object({
  date: isoDateString,
});
export type TicketTypesQuery = z.infer<typeof ticketTypesQuerySchema>;

export const ticketTypesResponseSchema = z.object({
  visitDate: isoDateString,
  ticketTypes: z.array(ticketTypeForKioskSchema),
  museumOpen: z.boolean(),
  museumClosedReason: z.string().nullable(),
});
export type TicketTypesResponse = z.infer<typeof ticketTypesResponseSchema>;

export const createOrderRequestSchema = z.object({
  visitDate: isoDateString,
  items: z
    .array(
      z.object({
        ticketTypeId: z.number().int().positive(),
        quantity: z.number().int().positive(),
      })
    )
    .min(1),
});
export type CreateOrderRequest = z.infer<typeof createOrderRequestSchema>;

export const createOrderResponseSchema = z.object({
  orderId: z.number().int().positive(),
  paymentId: z.number().int().positive(),
  amountCLP: z.number().int().nonnegative(),
  ticketNumberForIm30: z.string().length(20),
});
export type CreateOrderResponse = z.infer<typeof createOrderResponseSchema>;

export const confirmOrderRequestSchema = z.object({
  im30: im30SaleResponseSchema,
});
export type ConfirmOrderRequest = z.infer<typeof confirmOrderRequestSchema>;

export const printableTicketSchema = z.object({
  id: z.number().int().positive(),
  displayTicketNumber: z.string(),
  uniqueCode: z.string(),
  ticketTypeName: z.string(),
  visitDate: isoDateString,
  priceCLP: z.number().int().nonnegative(),
});
export type PrintableTicket = z.infer<typeof printableTicketSchema>;

export const printPayloadResponseSchema = z.object({
  orderId: z.number().int().positive(),
  museumName: z.string(),
  voucher: z.string().nullable(),
  tickets: z.array(printableTicketSchema),
});
export type PrintPayloadResponse = z.infer<typeof printPayloadResponseSchema>;

// Upper bound for the raw IM30 frame a kiosk may attach to a cancel (a real
// 0210 is ~1 KB). The SPA truncates to this before sending.
export const MAX_IM30_RAW_CHARS = 4096;

export const cancelOrderRequestSchema = z.object({
  reason: z.enum(["user_cancel", "im30_decline", "timeout", "bridge_error"]),
  im30Raw: z.string().max(MAX_IM30_RAW_CHARS).optional(),
});
export type CancelOrderRequest = z.infer<typeof cancelOrderRequestSchema>;

export const cancelOrderResponseSchema = z.object({
  orderId: z.number().int().positive(),
  status: z.literal("cancelled"),
});
export type CancelOrderResponse = z.infer<typeof cancelOrderResponseSchema>;

export const heartbeatRequestSchema = z.object({
  printerOk: z.boolean(),
  im30Ok: z.boolean(),
  lastSaleNumber: z.string().nullable(),
  queueDepth: z.number().int().nonnegative(),
});
export type HeartbeatRequest = z.infer<typeof heartbeatRequestSchema>;

export const heartbeatResponseSchema = z.object({
  ok: z.literal(true),
  serverTime: z.string(),
});
export type HeartbeatResponse = z.infer<typeof heartbeatResponseSchema>;

export const dailyCloseRequestSchema = z.object({
  triggeredBy: z.enum(["cron", "admin"]),
});
export type DailyCloseRequest = z.infer<typeof dailyCloseRequestSchema>;

export const dailyCloseResponseSchema = z.object({
  ok: z.boolean(),
  voucher: z.string().nullable(),
});
export type DailyCloseResponse = z.infer<typeof dailyCloseResponseSchema>;

// --- Kiosk telemetry -------------------------------------------------------
// Best-effort event stream from the totem SPA. The backend writes each event
// as one structured log line (CloudWatch); nothing is persisted in the DB.
// See docs/kiosk/payment-telemetry.md for the triage queries.

export const kioskEventKindSchema = z.enum([
  "sale.started",
  "sale.outcome",
  "sale.parse_error",
  "confirm.failed",
  "order.cancelled",
  // Backend only: a cancel request that did not cancel the order.
  "order.cancel_failed",
]);
export type KioskEventKind = z.infer<typeof kioskEventKindSchema>;

export const kioskEventSchema = z.object({
  at: z.string().datetime(),
  kind: kioskEventKindSchema,
  orderId: z.number().int().positive().optional(),
  data: z.record(z.unknown()),
});
export type KioskEvent = z.infer<typeof kioskEventSchema>;

export const kioskEventsRequestSchema = z.object({
  events: z.array(kioskEventSchema).min(1).max(20),
});
export type KioskEventsRequest = z.infer<typeof kioskEventsRequestSchema>;

export const kioskEventsResponseSchema = z.object({
  accepted: z.number().int().nonnegative(),
});
export type KioskEventsResponse = z.infer<typeof kioskEventsResponseSchema>;
